"use client";

import {
  ApolloClient,
  ApolloError,
  type FetchResult,
  from,
  fromPromise,
  HttpLink,
  InMemoryCache,
  Observable,
  type Operation,
} from "@apollo/client";
import { loadDevMessages, loadErrorMessages } from "@apollo/client/dev";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import type { GraphQLFormattedError } from "graphql";

import {
  canRetryOriginalRequest,
  shouldClearSessionOnRefreshFailure,
  shouldRetryWithMemoryToken,
} from "@/libs/apollo/authRefreshDecision";
import { clearAnalyticsUserId } from "@/libs/google";
import type { RefreshAuthResult } from "@/util/auth/refreshAuthResult";
import { refreshAuthSession } from "@/util/cookies";

// Apollo Client 3.10+はバンドルサイズ削減のため、既定でエラーメッセージを
// go.apollo.dev のコード化リンクに置き換える。開発環境では実際の文言を
// 表示できるよう、起動時に一度だけ読み込む。
if (process.env.NODE_ENV !== "production") {
  loadDevMessages();
  loadErrorMessages();
}

/**
 * このモジュールは "use client" だが SSR でも評価され、そこでの変数は
 * Node プロセス全体（＝全ユーザーのリクエスト）で共有される。
 * 認証情報をリクエスト間で持ち回らないよう、サーバーでは一切触らない。
 */
const isServer = typeof window === "undefined";

/**
 * undefined: まだ未設定（初回hydrate可）
 * null: ログアウト等で明示クリア（古い props で復活させない）
 * string: 有効なアクセストークン
 *
 * ブラウザ専用。サーバーでは各リクエストの authToken（makeClient の引数）を使う。
 */
let accessTokenInMemory: string | null | undefined;
let refreshPromise: Promise<RefreshAuthResult> | null = null;

export function setAccessTokenInMemory(token: string | null) {
  if (isServer) return;
  accessTokenInMemory = token;
}

/**
 * 初回表示用。未設定のときだけ server 由来の token を載せる。
 * null（明示クリア）や string（リフレッシュ後など）があるときは上書きしない。
 */
export function hydrateAccessTokenFromServer(token: string | null | undefined) {
  if (isServer) return;
  if (accessTokenInMemory !== undefined) return;
  if (token) {
    accessTokenInMemory = token;
  }
}

function authorizationHeaderFromContext(
  headers: Record<string, unknown> | undefined,
): string | undefined {
  const value = headers?.Authorization ?? headers?.authorization;
  return typeof value === "string" ? value : undefined;
}

function retryOperationWithAccessToken(
  operation: Operation,
  accessToken: string,
) {
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      Authorization: `Bearer ${accessToken}`,
    },
    // リトライ後に再度 UNAUTHORIZED でもリフレッシュを繰り返さない
    skipAuthRefresh: true,
  }));
}

function isUnauthorizedError(graphQLErrors?: readonly GraphQLFormattedError[]) {
  return (
    graphQLErrors?.some(
      (err) => err.extensions?.code === "UNAUTHORIZED_ERROR",
    ) ?? false
  );
}

function refreshFailureMessage(result: RefreshAuthResult): string {
  if (result.status === "transient_error") {
    return "通信エラーが発生しました。時間をおいて再度お試しください";
  }
  return "認証の更新に失敗しました";
}

async function refreshAccessToken(): Promise<RefreshAuthResult> {
  // リフレッシュトークンは Server Action 内だけで扱い、ブラウザに出さない
  const result = await refreshAuthSession();
  if (result.status === "success") {
    setAccessTokenInMemory(result.accessToken);
  }
  return result;
}

function refreshAccessTokenOnce(): Promise<RefreshAuthResult> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * メモリ上のアクセストークンだけ捨てる。画面遷移は既存の GraphqlErrors に任せる。
 *
 * ここで cookie 削除（Server Action）を呼ばないのは意図的。
 * Server Action を呼ぶと Next.js が画面を再取得してルーターの状態が変わり、
 * 同時にエラー画面へ切り替わることで React のフック数が合わなくなって画面全体が落ちる
 * （Next.js の既知不具合。React 側は facebook/react#36911 で修正済みだが安定版へ未反映）。
 * cookie のトークンはサーバー側で既に無効なので、残っても認証は通らない。
 * ログアウト操作では従来どおり cookie を削除する（useLogout）。
 */
function clearSessionInMemory() {
  setAccessTokenInMemory(null);
  // 退会・利用停止・期限切れなど本人操作以外で認証が外れたとき、前のワーカーの user_id を残さない
  clearAnalyticsUserId();
}

function unauthorizedApolloError(
  graphQLErrors: readonly GraphQLFormattedError[] | undefined,
) {
  return new ApolloError({
    graphQLErrors: graphQLErrors ? [...graphQLErrors] : [],
  });
}

type RefreshLinkResult =
  | { status: "success"; accessToken: string }
  | { status: "fail"; result: RefreshAuthResult }
  /** 既存の「再ログインが必要です。」画面へ渡す */
  | { status: "unauthorized" };

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      });
    }
    if (networkError) console.log(`[Network error]: ${networkError}`);

    // SSR ではリフレッシュしない。cookie の書き換えはレンダー中に行えず、
    // refreshPromise / accessTokenInMemory もリクエスト間で共有されるため。
    // 期限切れの復帰は hydration 後のブラウザ側の再取得に任せる。
    if (isServer) return;

    if (
      !isUnauthorizedError(graphQLErrors) ||
      operation.operationName === "RefreshSession"
    ) {
      return;
    }

    // リトライ後も UNAUTHORIZED → 既存の GraphqlErrors（再ログイン案内）へ
    if (operation.getContext().skipAuthRefresh) {
      clearSessionInMemory();
      return new Observable<FetchResult>((observer) => {
        observer.error(unauthorizedApolloError(graphQLErrors));
      });
    }

    const failedAuthorization = authorizationHeaderFromContext(
      operation.getContext().headers as Record<string, unknown> | undefined,
    );
    const memoryToken = accessTokenInMemory;
    // リフレッシュ直後の古い応答。再リフレッシュせずメモリ上の新トークンで送り直す
    if (
      memoryToken &&
      shouldRetryWithMemoryToken({
        authorizationHeader: failedAuthorization,
        accessTokenInMemory: memoryToken,
      })
    ) {
      retryOperationWithAccessToken(operation, memoryToken);
      return forward(operation);
    }

    return fromPromise(
      refreshAccessTokenOnce().then((result): RefreshLinkResult => {
        if (shouldClearSessionOnRefreshFailure(result)) {
          clearSessionInMemory();
          return { status: "unauthorized" };
        }
        if (!canRetryOriginalRequest(result) || result.status !== "success") {
          // 通信障害ではセッションを消さず、元リクエストを失敗させる
          return { status: "fail", result };
        }
        const accessToken = result.accessToken;
        retryOperationWithAccessToken(operation, accessToken);
        return { status: "success", accessToken };
      }),
    ).flatMap((outcome) => {
      if (outcome.status === "unauthorized") {
        return new Observable<FetchResult>((observer) => {
          observer.error(unauthorizedApolloError(graphQLErrors));
        });
      }
      if (outcome.status !== "success") {
        return new Observable<FetchResult>((observer) => {
          observer.error(new Error(refreshFailureMessage(outcome.result)));
        });
      }
      return forward(operation);
    });
  },
);

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 10000,
    jitter: true,
  },
  attempts: {
    max: 5,
    // ネットワークエラーまたはサーバーエラーの場合のみ再試行
    retryIf: (error: any, _operation: Operation) => {
      if (error.networkError) {
        // タイムアウトエラーの場合のみ再試行
        if (error.networkError.statusCode === 408) {
          return true;
        }
        // その他のネットワークエラーは再試行しない
        return false;
      }
      if (error.graphQLErrors) {
        return error.graphQLErrors.some(
          (err: GraphQLFormattedError) =>
            err.extensions?.code === "SERVER_ERROR",
        );
      }
      return false;
    },
  },
});

/**
 * authTokenがあるときだけ初回hydrateする。引数なし／空は既存トークンを消さない。
 *
 * SSR ではモジュール変数が全リクエストで共有されるため、このクライアント
 * （＝この一度のレンダー）に閉じた requestToken だけを使う。
 */
export function makeClient(options?: { authToken?: string | null }) {
  const requestToken = options?.authToken ?? null;
  if (options?.authToken) {
    hydrateAccessTokenFromServer(options.authToken);
  }

  const authLink = setContext((_, { headers }) => {
    const token = isServer ? requestToken : accessTokenInMemory;
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : "",
      },
    };
  });

  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT,
    fetchOptions: { cache: "no-store" },
  });

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: from([errorLink, retryLink, authLink, httpLink]),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-and-network",
      },
    },
  });
}
